import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { X, Calendar, Gauge, Fuel, Info, Settings, MapPin, ChevronLeft, ChevronRight, Phone, ExternalLink } from 'lucide-react';
import VehicleImageCarousel from './VehicleImageCarousel';

const getYoutubeEmbedUrl = (url) => {
  if (!url) return null;
  const regExp = /^.*(youtu.be\/|v\/|u\/\w\/|embed\/|watch\?v=|&v=)([^#&?]*).*/;
  const match = url.match(regExp);
  return (match && match[2].length === 11) ? `https://www.youtube.com/embed/${match[2]}` : null;
};

const VehicleModal = ({ vehicle, onClose }) => {
  const navigate = useNavigate();

  // Bloquer le scroll du body quand la modale est ouverte
  useEffect(() => {
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = 'auto';
    };
  }, []);

  if (!vehicle) return null;

  return (
    <div className="modal-backdrop" onClick={onClose}>
      <div className="modal-content" onClick={(e) => e.stopPropagation()}>
        <button className="close-btn" onClick={onClose}>
          <X size={24} />
        </button>

        <div className="modal-layout">
          {/* Section Gauche : Galerie d'images */}
          <div className="modal-gallery">
            <VehicleImageCarousel 
              images={[...(vehicle.cover_image ? [vehicle.cover_image] : []), ...(vehicle.images || [])]} 
              carId={vehicle.id} 
              altText={`${vehicle.make} ${vehicle.model}`}
              thumbSize="800x600"
            />
          </div>

          {/* Section Droite : Informations */}
          <div className="modal-info">
            <span className="brand-badge">{vehicle.make} {vehicle.model}</span>
            <h2 className="vehicle-title">{vehicle.title}</h2>
            <div className="vehicle-price">{vehicle.price} €</div>

            <p className="vehicle-desc">
              {vehicle.description || "Aucune description détaillée n'a été fournie pour ce véhicule."}
            </p>

            {vehicle.youtube_link && getYoutubeEmbedUrl(vehicle.youtube_link) && (
              <div className="video-section">
                <div className="video-container">
                  <iframe
                    src={getYoutubeEmbedUrl(vehicle.youtube_link)}
                    title="Vidéo du véhicule"
                    frameBorder="0"
                    allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                    allowFullScreen>
                  </iframe>
                </div>
              </div>
            )}

            <div className="specs-grid">
              <div className="spec-card">
                <Calendar size={20} />
                <div className="spec-text">
                  <span className="spec-label">Année</span>
                  <span className="spec-value">{vehicle.year}</span>
                </div>
              </div>
              <div className="spec-card">
                <Gauge size={20} />
                <div className="spec-text">
                  <span className="spec-label">Kilométrage</span>
                  <span className="spec-value">{vehicle.mileage} km</span>
                </div>
              </div>
              <div className="spec-card">
                <Fuel size={20} />
                <div className="spec-text">
                  <span className="spec-label">Carburant</span>
                  <span className="spec-value">{vehicle.fuel_type || '-'}</span>
                </div>
              </div>
              <div className="spec-card">
                <Settings size={20} />
                <div className="spec-text">
                  <span className="spec-label">Boîte</span>
                  <span className="spec-value">{vehicle.transmission === 'automatic' ? 'Automatique' : 'Manuelle'}</span>
                </div>
              </div>
            </div>

            <div className="modal-actions">
              <button
                className="btn-full-page"
                onClick={() => {
                  onClose();
                  navigate(`/vente-auto/${vehicle.id}`);
                }}
              >
                <ExternalLink size={20} />
                Voir la page complète
              </button>

              <a href="tel:+33600000000" className="contact-btn">
                <Phone size={20} />
                Appeler pour ce véhicule
              </a>
              <p className="contact-note">Notre équipe est à votre disposition pour programmer un essai.</p>
            </div>
          </div>
        </div>
      </div>

      <style jsx>{`
        .modal-backdrop {
          position: fixed;
          inset: 0;
          background: rgba(15, 23, 42, 0.85);
          backdrop-filter: blur(4px);
          z-index: 1000;
          display: flex;
          align-items: center;
          justify-content: center;
          padding: 20px;
        }

        .modal-content {
          background: white;
          width: 100%;
          max-width: 1000px;
          border-radius: 24px;
          overflow: hidden;
          position: relative;
          box-shadow: 0 25px 50px -12px rgba(0, 0, 0, 0.25);
          max-height: 90vh;
          display: flex;
          flex-direction: column;
        }

        .close-btn {
          position: absolute;
          top: 20px;
          right: 20px;
          width: 40px;
          height: 40px;
          border-radius: 50%;
          background: white;
          border: none;
          display: flex;
          align-items: center;
          justify-content: center;
          cursor: pointer;
          z-index: 10;
          color: #1e293b;
          box-shadow: 0 4px 6px -1px rgba(0,0,0,0.1);
          transition: all 0.2s;
        }

        .close-btn:hover {
          background: #f1f5f9;
          transform: scale(1.05);
        }

        .modal-layout {
          display: flex;
          flex-direction: row;
          height: 100%;
          overflow-y: auto;
        }

        .modal-gallery {
          flex: 1.2;
          background: #f8fafc;
          padding: 20px;
          display: flex;
          flex-direction: column;
          gap: 15px;
        }

        .main-image-container {
          position: relative;
          width: 100%;
          height: 400px;
          border-radius: 16px;
          overflow: hidden;
          background: #e2e8f0;
        }

        .main-image {
          width: 100%;
          height: 100%;
          object-fit: contain;
        }

        .nav-btn {
          position: absolute;
          top: 50%;
          transform: translateY(-50%);
          width: 40px;
          height: 40px;
          border-radius: 50%;
          background: rgba(255,255,255,0.9);
          border: none;
          display: flex;
          align-items: center;
          justify-content: center;
          cursor: pointer;
          color: #1e293b;
          transition: 0.2s;
        }
        .nav-btn:hover { background: white; }
        .nav-btn.prev { left: 15px; }
        .nav-btn.next { right: 15px; }

        .thumbnails {
          display: flex;
          gap: 10px;
          overflow-x: auto;
          padding-bottom: 5px;
        }

        .thumb-btn {
          width: 80px;
          height: 60px;
          border-radius: 8px;
          border: 2px solid transparent;
          overflow: hidden;
          cursor: pointer;
          padding: 0;
          flex-shrink: 0;
          opacity: 0.7;
          transition: 0.2s;
        }
        .thumb-btn.active { border-color: #3b82f6; opacity: 1; }
        .thumb-btn img { width: 100%; height: 100%; object-fit: cover; }

        .no-image {
          height: 400px; display: flex; flex-direction: column; align-items: center; justify-content: center;
          color: #94a3b8; background: #e2e8f0; border-radius: 16px;
        }
        .no-icon { font-size: 4rem; margin-bottom: 10px; }

        .modal-info {
          flex: 1;
          padding: 40px;
          display: flex;
          flex-direction: column;
        }

        .brand-badge {
          display: inline-block;
          background: #eff6ff;
          color: #2563eb;
          padding: 6px 14px;
          border-radius: 50px;
          font-weight: 700;
          font-size: 0.85rem;
          text-transform: uppercase;
          letter-spacing: 1px;
          margin-bottom: 15px;
          align-self: flex-start;
        }

        .vehicle-title {
          font-size: 2.2rem;
          font-weight: 900;
          color: #1e293b;
          margin-bottom: 10px;
          line-height: 1.2;
        }

        .vehicle-price {
          font-size: 2rem;
          font-weight: 800;
          color: #3b82f6;
          margin-bottom: 25px;
        }

        .vehicle-desc {
          color: #475569;
          line-height: 1.6;
          margin-bottom: 30px;
          font-size: 1.05rem;
        }

        .video-section { margin-bottom: 30px; }
        .video-container { position: relative; padding-bottom: 56.25%; height: 0; overflow: hidden; border-radius: 12px; background: #e2e8f0; }
        .video-container iframe { position: absolute; top: 0; left: 0; width: 100%; height: 100%; border-radius: 12px; }

        .specs-grid {
          display: grid;
          grid-template-columns: 1fr 1fr;
          gap: 20px;
          margin-bottom: 40px;
        }

        .spec-card {
          display: flex;
          align-items: center;
          gap: 15px;
          background: #f8fafc;
          padding: 15px;
          border-radius: 12px;
          color: #3b82f6;
        }

        .spec-text { display: flex; flex-direction: column; }
        .spec-label { color: #64748b; font-size: 0.8rem; text-transform: uppercase; letter-spacing: 1px; font-weight: 600; margin-bottom: 2px; }
        .spec-value { color: #1e293b; font-weight: 700; font-size: 1rem; }

        .modal-actions {
          margin-top: auto;
        }

        .contact-btn {
          display: flex;
          align-items: center;
          justify-content: center;
          gap: 10px;
          background: #10b981;
          color: white;
          text-decoration: none;
          padding: 18px;
          border-radius: 14px;
          font-weight: 800;
          font-size: 1.1rem;
          text-transform: uppercase;
          letter-spacing: 1px;
          transition: all 0.3s;
          box-shadow: 0 10px 20px -10px rgba(16, 185, 129, 0.5);
        }

        .contact-btn:hover {
          transform: translateY(-2px);
          box-shadow: 0 15px 25px -10px rgba(16, 185, 129, 0.6);
        }

        .btn-full-page {
          display: flex;
          align-items: center;
          justify-content: center;
          gap: 8px;
          background: white;
          color: #3b82f6;
          border: 2px solid #3b82f6;
          padding: 14px;
          border-radius: 14px;
          font-weight: 700;
          font-size: 1rem;
          cursor: pointer;
          transition: all 0.2s;
          margin-bottom: 12px;
          width: 100%;
        }

        .btn-full-page:hover {
          background: #eff6ff;
        }

        .contact-note {
          text-align: center;
          color: #94a3b8;
          font-size: 0.9rem;
          margin-top: 15px;
        }

        @media (max-width: 768px) {
          .modal-layout { flex-direction: column; }
          .modal-info { padding: 25px; }
          .main-image-container { height: 250px; }
          .specs-grid { grid-template-columns: 1fr; }
        }
      `}</style>
    </div>
  );
};

export default VehicleModal;
